/**
 * **滚动区** —— 内容长度由数据决定的地方必须用它。
 *
 * 实现是 Pixi 里最省事的那种：内容装一个容器、整体上下平移、外面套一个矩形遮罩。
 * 右侧画一条**实心滚动条**（印刷语言里没有「细圆角滚动条」这种东西）。
 *
 * ⚠️ 早先房间页上是**两个独立面板**（游戏规则一个、牌堆一个），结果是右边的内容
 * 比 800px 的画布高得多，两块面板必然互相盖住。现在整列收进**一个**滚动区：
 * 面板高度固定，内容在里面滚。**靠算高度去挤，迟早会被多出来的一行挤爆。**
 *
 * 滚轮挂在自己身上；**触控拖动、越界橡皮筋、松手惯性与回弹**交给 `pan.ts`
 * （抽牌堆横排、记牌器网格共用同一套手势与物理）。
 */

import { Container, Graphics, Rectangle } from 'pixi.js';
import type { FederatedWheelEvent } from 'pixi.js';
import { INK, uiPad } from './style';
import { attachPan, type Pan } from './pan';
import type { Widget } from './base';

export interface ScrollAreaSpec {
  readonly scale: number;
  readonly width: number;
  readonly height: number;
  /**
   * **遮罩向左右各多留这么多。**
   *
   * 内容从 x=0 开始，遮罩也只从 x=0 开始的话，内容最左/最右那条边正好压在遮罩
   * 边界上，会被裁掉一条（按钮、边框最明显）。留出 bleed 之后两边都有余量。
   */
  readonly bleed?: number;
}

export interface ScrollArea extends Widget {
  readonly content: Container;
  /** 告诉它内容有多高（内容摆好之后调） */
  setContentHeight(height: number): void;
  scrollTo(y: number): void;
  readonly scrollY: number;
  /** 摘掉拖动可能挂在根上的监听并销毁。`destroyWidget` 走不到这里，它只在容器销毁时兜底 */
  destroy(): void;
}

export function scrollArea(spec: ScrollAreaSpec): ScrollArea {
  const { scale, width, height } = spec;
  const bleed = spec.bleed ?? 0;
  const container = new Container();
  const content = new Container();
  const maskShape = new Graphics()
    .rect(-bleed, 0, width + bleed * 2, height)
    .fill({ color: 0xffffff });
  const bar = new Graphics();
  const barWidth = uiPad(3, scale);

  container.addChild(content, maskShape, bar);
  content.mask = maskShape;

  let contentHeight = height;
  let offset = 0;
  let visible = true;

  const maxScroll = (): number => Math.max(0, contentHeight - height);

  const paintBar = (): void => {
    const max = maxScroll();
    visible = max > 0.5;
    bar.visible = visible;
    if (!visible) return;
    const ratio = height / contentHeight;
    const barHeight = Math.max(uiPad(24, scale), height * ratio);
    const travel = height - barHeight;
    const shown = clamp(offset, -max, 0);
    const y = max === 0 ? 0 : (-shown / max) * travel;
    bar
      .clear()
      .rect(width - barWidth, y, barWidth, barHeight)
      .fill({ color: INK.primary, alpha: 0.75 });
  };

  const applyOffset = (y: number): void => {
    offset = y;
    content.y = y;
    paintBar();
  };

  let pan: Pan | null = null;
  const scrollTo = (y: number): void => {
    pan?.stop();
    applyOffset(clamp(y, -maxScroll(), 0));
  };

  container.eventMode = 'static';
  container.hitArea = new Rectangle(-bleed, 0, width + bleed * 2, height);
  container.on('wheel', (event: FederatedWheelEvent) => {
    if (!visible) return;
    event.preventDefault?.();
    scrollTo(offset - event.deltaY);
  });

  pan = attachPan({
    surface: container,
    axis: 'y',
    scale,
    bounds: () => ({ min: -maxScroll(), max: 0 }),
    read: () => offset,
    write: (value) => applyOffset(value),
  });

  paintBar();

  return {
    container,
    content,
    width,
    height,
    get scrollY() {
      return offset;
    },
    setContentHeight(next: number) {
      contentHeight = next;
      // 内容变短了要把它拉回来，否则会停在「滚过头」的位置、底下一片空白
      scrollTo(offset);
    },
    scrollTo,
    destroy() {
      pan?.destroy();
      container.destroy({ children: true });
    },
  };
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return value < min ? min : value > max ? max : value;
}
